import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { EnrichmentRunSelection } from '~/components/common/enrichment-run-panel';
import type { AlreadyCuratedPreflightSnapshot } from '~/features/already-curated/types/already-curated.types';
import type { PlannedCost } from '~/features/cost-estimate/types/cost-estimate.types';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';
import type { NotEnrichedChoice } from '~/features/not-enriched/components/not-enriched-carryover';
import type { ProductGroupsChoice } from '~/features/product-groups/types/product-groups.types';
import { productClassFields } from '~/features/product-groups/utils/product-groups';
import { useAuthStore } from '~/store/auth.store';
import { extractApiError } from '~/utils/extract-api-error';

import type { ScheduledEnrichmentPlatform } from '../config/scheduled-enrichment-platform';
import {
  createScheduledJob,
  triggerScheduledSampleEnrichment,
} from '../services/scheduled-enrichment.service';
import type { ScheduledEnrichmentStore } from '../store/create-scheduled-enrichment-store';
import { buildEnrichmentRunPayload } from '../utils/enrichment-run-payload';
import { sampleTriggerPayload } from '../utils/sample-trigger-payload';

/** What Start sends: the panel selection, plus the cost the stepper showed (lily.ai only). */
export type CreateEnrichmentRunInput = EnrichmentRunSelection & {
  plannedCost?: PlannedCost | null;
  /**
   * The class counts on screen when Start was pressed. The choice's own counts are a snapshot
   * from when a group was last toggled (stale after a filter change, null if toggled while
   * counting), so a surface that knows the current split passes it here and it wins.
   */
  productClassCounts?: Record<string, number> | null;
};

interface UseCreateEnrichmentRunArgs {
  /** Platform descriptor (Google or Meta) — supplies connector, use-case and routes. */
  config: ScheduledEnrichmentPlatform;
  /** Persisted scheduled-enrichment store for that platform; hydrated before navigation. */
  store: ScheduledEnrichmentStore;
  /** Catalog connection the batch runs against. */
  connectionId: string | null;
  /** Catalog snapshot pin (null → the backend resolves the latest sync). */
  syncId: string | null;
  /** Applied catalog filters scoping the batch; empty = the whole catalog. */
  filters: AppliedProductFilters;
  /** Pre-flight already-curated breakdown (③) for the scope being run, captured
   *  on the selection screen. Persisted so the post-run timeline + Enrichment
   *  Overview can render a faithful "skipped" summary. `null`/omitted when the
   *  breakdown was still running at proceed time. */
  preflightSnapshot?: AlreadyCuratedPreflightSnapshot | null;
  /** Fired after the store is hydrated and before navigation (e.g. to close the panel). */
  onCreated?: () => void;
  /**
   * The "Not enriched yet" choice made on THIS surface (the product-selection page). Bound to the
   * new batch at create. Omitted by surfaces that never show the section, so a choice left in the
   * store from another batch is never carried into this one.
   */
  carryover?: NotEnrichedChoice | null;
  /**
   * The product groups included on THIS surface (curation classes and not-enriched buckets).
   * Bound to the new batch at create, like {@link carryover}; omitted by surfaces that never
   * show the groups.
   */
  productGroups?: ProductGroupsChoice | null;
}

/**
 * Creates a scheduled enrichment batch from an enrichment-run panel selection and
 * hands off to the platform's content-enrichment funnel: create the job → start its
 * sample → hydrate the platform store → navigate to the batch. A sample that fails to
 * start leaves the batch created: the toast says so and the batch's "Generate Sample
 * Content" is the retry.
 *
 * One hook for every creation surface. The Catalog Operations product-selection
 * page and the Product Catalog header action previously carried separate copies
 * of this same two-request sequence, which meant every field added to a run had
 * to be added twice — and the post-step flags were once added to only one.
 */
export function useCreateEnrichmentRun({
  config,
  store,
  connectionId,
  syncId,
  filters,
  preflightSnapshot,
  onCreated,
  carryover = null,
  productGroups = null,
}: UseCreateEnrichmentRunArgs) {
  const navigate = useNavigate();
  const { t } = useTranslation('content-enrichment');
  const subsidiaryId = useAuthStore((s) => s.user?.activeTenantId ?? '');

  return useMutation({
    mutationFn: async ({
      plannedCost,
      productClassCounts,
      ...selection
    }: CreateEnrichmentRunInput) => {
      // The groups as they stand at submit: the current counts replace the toggle-time snapshot.
      const groups =
        productGroups && productClassCounts !== undefined
          ? { ...productGroups, classCounts: productClassCounts }
          : productGroups;
      if (!connectionId) throw new Error(`${config.connectionCode} connection not found`);

      const { name, runConfig, overrideCache, ingestMode, idempotencyKey } =
        buildEnrichmentRunPayload(selection);
      // Spread as a slice so an unset scope stays off the wire entirely.
      const ingest = ingestMode !== undefined ? { ingestMode } : {};
      const hasFilters = Object.keys(filters).length > 0;

      // `runConfig` and `overrideCache` are spread identically onto both requests:
      // an enrichment that scored one attribute set and enriched another is not a
      // preview of itself, and a cache-bypass must apply to the whole run.
      const job = await createScheduledJob({
        connectorCode: config.connectorCode,
        useCase: config.useCase,
        ...(name !== undefined ? { name } : {}),
        ...(idempotencyKey ? { idempotencyKey } : {}),
        ...(syncId ? { syncId } : {}),
        ...(hasFilters ? { filters } : {}),
        ...runConfig,
        overrideCache,
        ...ingest,
        // Saved at create so the batch shows its groups (and planned cost) from the start.
        ...(groups ? productClassFields(groups, ingestMode) : {}),
        ...(plannedCost ? { plannedCost } : {}),
      });

      // No raw content-scoring pass: "Score before enrichment" is the curated PRE the sample
      // (10) and the full run (90) take, capped at 100 a phase (workflow #1040). The server
      // reads the step as SKIPPED, which the batch screens already treat as done. So the sample
      // is the run's first step: start it now, with the body "Generate Sample Content" sends.
      // Its failure must not lose the created batch, so it is caught and reported, not thrown.
      let sampleWorkflowId: string | null = null;
      let sampleError: unknown = null;
      try {
        const sample = await triggerScheduledSampleEnrichment(
          sampleTriggerPayload({
            connectorCode: config.connectorCode,
            useCase: config.useCase,
            scheduledJobId: job.jobId,
            subsidiaryId,
            syncId,
            filters,
            runConfig,
            overrideCache,
            ingestMode: ingestMode ?? null,
          }),
        );
        sampleWorkflowId = sample.workflowId;
      } catch (err) {
        sampleError = err;
      }

      return { job, runConfig, overrideCache, ingestMode, sampleWorkflowId, sampleError, groups };
    },
    onSuccess: ({
      job,
      runConfig,
      overrideCache,
      ingestMode,
      sampleWorkflowId,
      sampleError,
      groups,
    }) => {
      // Setters are read imperatively so a caller can pick the store by platform
      // without a conditional hook call.
      const state = store.getState();
      state.reset();
      // Only a choice made on the creating surface survives, bound to this batch: the full-catalog
      // trigger (a later page) sends it only for this batch and subsidiary.
      state.setCarryover(carryover ? { ...carryover, jobId: job.jobId } : null);
      // The full-catalog trigger re-sends these counts, so it keeps the submit-time ones too.
      state.setProductGroups(groups ? { ...groups, jobId: job.jobId } : null);
      state.setScheduledJobId(job.jobId);
      // The batch opens on its running sample; without one it opens where "Generate Sample
      // Content" retries it.
      if (sampleWorkflowId) state.setSampleEnrichmentWorkflowId(sampleWorkflowId);
      if (sampleError) {
        // Always say the batch exists and how to retry; the server's reason rides underneath.
        const reason = extractApiError(sampleError, '');
        toast.error(t('createRun.sampleNotStarted'), reason ? { description: reason } : undefined);
      }
      // Persisted so the sample and full-catalog triggers — fired from later pages,
      // possibly after a reload — reproduce the configuration chosen in the panel.
      state.setRunConfig(runConfig);
      state.setOverrideCache(overrideCache);
      state.setIngestMode(ingestMode ?? null);
      if (connectionId) state.setConnectionId(connectionId);
      if (syncId) state.setSyncId(syncId);
      if (Object.keys(filters).length > 0) state.setFilters(filters);
      // Persist the pre-flight breakdown (when captured) so the post-run
      // surfaces can explain how many products were skipped as already-curated.
      if (preflightSnapshot) state.setPreflightSnapshot(preflightSnapshot);
      onCreated?.();
      // A started sample opens on Review (its loader shows progress), skipping the Current Score
      // step whose only action was starting it; without one, that step's "Generate Sample
      // Content" is the retry.
      const step = sampleWorkflowId ? '/review' : '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      void navigate({ to: `${config.contentEnrichmentBase}/${job.jobId}${step}` as any });
    },
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to start enrichment. Please try again.'));
    },
  });
}
